import { useEffect, useRef, useState } from 'react';
import { IconPhotoOff, IconRefresh } from '@tabler/icons-react';
import {
  Button,
  InlineError,
  Stack,
  Text,
  ThumbnailImagePicker,
  ImageLightbox,
  WP_IMAGE_FILE_ACCEPT,
  useDebouncedValue,
} from '@wapharma/design-system';
import type { GalleryImagePickerItem } from '@wapharma/design-system';
import { unwrapData } from '@wapharma/shared';
import { apiGet, apiPost, apiPostForm } from '../api';
import { getDisplayError } from '../apiErrors';
import { parseCatalogMediaLibraryItem } from '../lib/catalogMediaLibrary';
import { resolveApiMediaUrl } from '../lib/resolveApiMediaUrl';
import {
  fetchCatalogProxyImageFile,
  isAllowedImageFile,
} from '../pages/catalog/catalogPageWizardHelpers';
import styles from './MarkeLogoField.module.css';

export type MarkeLogoValue = { key: string | null; url: string | null };

export type MarkeLogoFieldProps = {
  token: string | null;
  value: MarkeLogoValue;
  onChange: (next: MarkeLogoValue) => void;
  disabled?: boolean;
  galleryItems?: GalleryImagePickerItem[];
  /** Brand name — Wikipedia/Wikidata logo lookup when the field is empty / still auto-filled. */
  lookupName?: string;
  /**
   * Taxonomy structure wizard: free-text Pexels suggest (CDN preview, key stays null until import).
   * Mutually exclusive with `lookupName` (Pexels wins when both set).
   */
  pexelsSuggestQuery?: string;
  /** Compact list/edit, large wizard stage, full-width cover banner, or beside-name create row. */
  presentation?: 'compact' | 'stage' | 'cover' | 'inline';
  /** True while auto logo lookup / upload runs (incl. name debounce). */
  onBusyChange?: (busy: boolean) => void;
};

type PexelsSuggestBody = {
  query: string;
  imageUrl: string | null;
  index: number;
  total: number;
};

export function MarkeLogoThumb({
  url,
  name,
  size = 'sm',
  /** Click opens full-size inspect lightbox (default on when URL present). */
  zoomable = true,
}: {
  url: string | null | undefined;
  name: string;
  size?: 'sm' | 'lg';
  zoomable?: boolean;
}) {
  const [broken, setBroken] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [url]);

  const resolvedUrl = resolveApiMediaUrl(url) ?? url;
  const sizeClass = size === 'lg' ? ` ${styles.thumbLg}` : '';
  const showImg = Boolean(resolvedUrl) && !broken;
  const canZoom = zoomable && showImg;

  if (showImg) {
    const img = (
      <img
        className={`${styles.thumb}${sizeClass}`}
        src={resolvedUrl!}
        alt=""
        title={canZoom ? `${name} — cliquer pour agrandir` : name}
        onError={() => setBroken(true)}
      />
    );
    return (
      <>
        {canZoom ? (
          <button
            type="button"
            className={`${styles.thumbZoomBtn} nodrag nopan`}
            aria-label={`Agrandir ${name}`}
            title={`${name} — cliquer pour agrandir`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setZoomOpen(true);
            }}
          >
            {img}
          </button>
        ) : (
          img
        )}
        {canZoom ? (
          <ImageLightbox
            open={zoomOpen}
            onClose={() => setZoomOpen(false)}
            src={resolvedUrl!}
            label={name}
            caption={name}
          />
        ) : null}
      </>
    );
  }

  return (
    <span className={`${styles.thumbEmpty}${sizeClass}`} aria-hidden title={name}>
      <IconPhotoOff size={size === 'lg' ? 16 : 14} stroke={1.8} />
    </span>
  );
}

export function MarkeLogoField({
  token,
  value,
  onChange,
  disabled,
  galleryItems,
  lookupName,
  pexelsSuggestQuery,
  presentation = 'compact',
  onBusyChange,
}: MarkeLogoFieldProps) {
  const [uploadBusy, setUploadBusy] = useState(false);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [cycleBusy, setCycleBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [pexelsMeta, setPexelsMeta] = useState<{ index: number; total: number } | null>(null);
  const pexelsMode = pexelsSuggestQuery !== undefined;
  const brandMode = !pexelsMode && lookupName !== undefined;
  const [ownedByUser, setOwnedByUser] = useState(
    () => !pexelsMode && !brandMode && Boolean(value.key || value.url),
  );
  // Live lookup: never treat an existing preview as a manual pick — name edits must re-search.
  // Edit-without-lookup: keep existing asset as user-owned.
  const originRef = useRef<'auto' | 'user' | null>(
    pexelsMode || brandMode ? null : value.key || value.url ? 'user' : null,
  );
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const liveName = (pexelsMode ? pexelsSuggestQuery : lookupName)?.trim() ?? '';
  const [debouncedName] = useDebouncedValue(liveName, 450);
  const lookupGenRef = useRef(0);
  const autoForNameRef = useRef<string | null>(null);
  const stage = presentation === 'stage';
  const cover = presentation === 'cover';
  const inline = presentation === 'inline';
  const brandLabel = liveName;
  const imageNoun = pexelsMode ? 'image' : 'logo';
  const pickerSize = cover ? 'banner' : stage ? 'lg' : 'sm';

  const awaitingDebounce =
    (pexelsMode || brandMode) &&
    !disabled &&
    !ownedByUser &&
    liveName.length >= 2 &&
    liveName !== debouncedName;

  const fieldBusy = uploadBusy || lookupBusy || cycleBusy || awaitingDebounce;

  useEffect(() => {
    onBusyChange?.(fieldBusy);
  }, [fieldBusy, onBusyChange]);

  const markUserOrigin = () => {
    originRef.current = 'user';
    setOwnedByUser(true);
    autoForNameRef.current = null;
    setLookupBusy(false);
    setPexelsMeta(null);
  };

  const uploadFile = async (file: File, origin: 'auto' | 'user', isStale?: () => boolean) => {
    if (!token) {
      setErr('Non authentifié.');
      return false;
    }
    if (!isAllowedImageFile(file)) {
      setErr('Image JPEG, PNG, WebP, GIF ou AVIF.');
      return false;
    }
    setErr(null);
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const created = parseCatalogMediaLibraryItem(
        await apiPostForm<unknown>('/catalog/admin/media-library', fd, token),
      );
      if (isStale?.() || (origin === 'auto' && originRef.current === 'user')) return false;
      originRef.current = origin;
      if (origin === 'user') setOwnedByUser(true);
      setPexelsMeta(null);
      onChangeRef.current({ key: created.key, url: created.url });
      return true;
    } catch (e: unknown) {
      setErr(getDisplayError(e));
      return false;
    } finally {
      setUploadBusy(false);
    }
  };

  useEffect(() => {
    if ((!pexelsMode && !brandMode) || !token || disabled) {
      setLookupBusy(false);
      return;
    }
    const name = debouncedName;
    if (name.length < 2) {
      if (!valueRef.current.key) originRef.current = null;
      if (originRef.current === 'auto') {
        originRef.current = null;
        autoForNameRef.current = null;
        setPexelsMeta(null);
        onChangeRef.current({ key: null, url: null });
      }
      setLookupBusy(false);
      return;
    }
    // Manual upload/gallery/remove wins — do not overwrite.
    if (originRef.current === 'user') {
      setLookupBusy(false);
      return;
    }
    // Same name already auto-filled — skip duplicate fetch (token flicker, remount).
    if (originRef.current === 'auto' && autoForNameRef.current === name && valueRef.current.url) {
      setLookupBusy(false);
      return;
    }

    const gen = ++lookupGenRef.current;
    const ac = new AbortController();
    setLookupBusy(true);
    void (async () => {
      try {
        if (pexelsMode) {
          const raw = await apiGet<{ data?: PexelsSuggestBody } | PexelsSuggestBody>(
            `/catalog/admin/taxonomy/pexels/suggest?q=${encodeURIComponent(name)}`,
            token,
            ac.signal,
          );
          if (lookupGenRef.current !== gen) return;
          const body = unwrapData(raw) as PexelsSuggestBody;
          const url = body?.imageUrl?.trim() || null;
          if (!url) {
            if (originRef.current === 'auto' || originRef.current == null) {
              originRef.current = null;
              autoForNameRef.current = null;
              setPexelsMeta(null);
              onChangeRef.current({ key: null, url: null });
            }
            return;
          }
          if (originRef.current === 'user') return;
          originRef.current = 'auto';
          autoForNameRef.current = name;
          setPexelsMeta(
            body.total > 0 ? { index: body.index, total: body.total } : null,
          );
          // CDN preview only — key filled on wizard submit via /pexels/import.
          onChangeRef.current({ key: null, url });
          return;
        }

        const raw = await apiGet<{ data?: { url: string | null } }>(
          `/catalog/admin/external-catalog/brand-logo?name=${encodeURIComponent(name)}`,
          token,
          ac.signal,
        );
        if (lookupGenRef.current !== gen) return;
        const url = unwrapData<{ url: string | null }>(raw)?.url?.trim() || null;
        if (!url) {
          if (originRef.current === 'auto' || originRef.current == null) {
            originRef.current = null;
            autoForNameRef.current = null;
            onChangeRef.current({ key: null, url: null });
          }
          return;
        }
        const file = await fetchCatalogProxyImageFile(url, token);
        if (lookupGenRef.current !== gen) return;
        if (!file) {
          setErr('Logo trouvé mais téléchargement impossible (proxy image).');
          return;
        }
        const ok = await uploadFile(file, 'auto', () => lookupGenRef.current !== gen);
        if (lookupGenRef.current !== gen) return;
        if (ok) {
          setErr(null);
          autoForNameRef.current = name;
        }
      } catch (e: unknown) {
        if (ac.signal.aborted || lookupGenRef.current !== gen) return;
        setErr(getDisplayError(e));
      } finally {
        if (lookupGenRef.current === gen) setLookupBusy(false);
      }
    })();

    return () => {
      lookupGenRef.current += 1;
      ac.abort();
    };
    // uploadFile closes over token; lookup keyed on name + auth.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pexelsMode, brandMode, debouncedName, token, disabled]);

  const cyclePexels = async () => {
    if (!token || !pexelsMode || disabled || ownedByUser) return;
    const q = liveName.trim();
    if (q.length < 2) return;
    setCycleBusy(true);
    setErr(null);
    try {
      const raw = await apiPost<{ data?: PexelsSuggestBody } | PexelsSuggestBody>(
        '/catalog/admin/taxonomy/pexels/next-photo',
        { query: q },
        token,
      );
      const body = unwrapData(raw) as PexelsSuggestBody;
      const url = body?.imageUrl?.trim() || null;
      if (!url) return;
      originRef.current = 'auto';
      autoForNameRef.current = q;
      setPexelsMeta(body.total > 0 ? { index: body.index, total: body.total } : null);
      onChangeRef.current({ key: null, url });
    } catch (e: unknown) {
      setErr(getDisplayError(e));
    } finally {
      setCycleBusy(false);
    }
  };

  const galleryOn = galleryItems != null;
  const canCyclePexels =
    pexelsMode && Boolean(value.url) && !ownedByUser && !value.key;
  const previewUrl = resolveApiMediaUrl(value.url) ?? value.url;

  const picker = (
    <ThumbnailImagePicker
      accept={WP_IMAGE_FILE_ACCEPT}
      previewUrl={previewUrl}
      size={pickerSize}
      chooseLabel={pexelsMode || cover ? 'Choisir une image' : 'Choisir un logo'}
      changeLabel={pexelsMode || cover ? "Changer l'image" : 'Changer le logo'}
      removeLabel={pexelsMode || cover ? "Retirer l'image" : 'Retirer le logo'}
      previewLabel={pexelsMode || cover ? "Agrandir l'image" : 'Agrandir le logo'}
      disabled={disabled || fieldBusy || !token}
      galleryItems={galleryOn ? galleryItems : undefined}
      selectedGalleryId={galleryOn ? value.key : undefined}
      onGallerySelect={
        galleryOn
          ? (id) => {
              const hit = galleryItems.find((i) => i.id === id);
              markUserOrigin();
              onChange({ key: id, url: hit?.url ?? value.url });
            }
          : undefined
      }
      onGalleryUpload={
        galleryOn
          ? (file) => {
              markUserOrigin();
              void uploadFile(file, 'user');
            }
          : undefined
      }
      onChange={
        galleryOn
          ? undefined
          : (e) => {
              const file = e.currentTarget.files?.[0] ?? null;
              e.currentTarget.value = '';
              if (file) {
                markUserOrigin();
                void uploadFile(file, 'user');
              }
            }
      }
      onRemove={() => {
        markUserOrigin();
        setErr(null);
        onChange({ key: null, url: null });
      }}
    />
  );

  const cycleBtn = canCyclePexels ? (
      <Button
        type="button"
        size="sm"
        variant={cover ? 'ghost' : 'soft'}
        className={cover ? styles.coverCycleBtn : styles.cycleBtn}
        disabled={disabled || fieldBusy || !token || (pexelsMeta != null && pexelsMeta.total <= 1)}
        onClick={() => void cyclePexels()}
        title="Autre photo Pexels"
        aria-label="Autre photo Pexels"
      >
        <IconRefresh size={14} stroke={1.7} aria-hidden />
        Autre photo
        {pexelsMeta && pexelsMeta.total > 0
          ? ` (${pexelsMeta.index + 1}/${pexelsMeta.total})`
          : ''}
      </Button>
    ) : null;

  if (cover) {
    return (
      <div className={styles.cover}>
        <div className={styles.coverFrame}>
          {picker}
          {cycleBtn ? <div className={styles.coverActions}>{cycleBtn}</div> : null}
          {fieldBusy ? (
            <div className={styles.coverBusy} aria-live="polite">
              Recherche photo…
            </div>
          ) : null}
        </div>
        {err ? <InlineError>{err}</InlineError> : null}
      </div>
    );
  }

  if (stage) {
    return (
      <div className={styles.stage}>
        {brandLabel ? (
          <Text size="sm" className={styles.stageBrand}>
            {brandLabel}
          </Text>
        ) : null}
        <div className={styles.stageTile}>{picker}</div>
        {cycleBtn}
        <Text size="sm" c="dimmed" className={styles.stageHint}>
          {value.url
            ? `Cliquez pour agrandir. Bouton échange pour changer ou retirer${pexelsMode ? ' · Pexels' : ''}.`
            : `Cliquez pour choisir un fichier, ou collez une image (Ctrl+V).`}
        </Text>
        {err ? <InlineError>{err}</InlineError> : null}
      </div>
    );
  }

  if (inline) {
    return (
      <div className={styles.inlineWrap}>
        <div className={styles.inlinePicker}>{picker}</div>
        {cycleBtn || err ? (
          <div className={styles.inlineMeta}>
            {cycleBtn}
            {err ? <InlineError>{err}</InlineError> : null}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <Stack gap="xs">
      <Text size="sm" className={styles.label}>
        {pexelsMode ? 'Image' : 'Logo'}
      </Text>
      {picker}
      {cycleBtn}
      {pexelsMode && !value.url && liveName.length >= 2 && !fieldBusy ? (
        <Text size="xs" c="dimmed">
          Suggestion Pexels indisponible — téléversez une {imageNoun} ou laissez vide.
        </Text>
      ) : null}
      {err ? <InlineError>{err}</InlineError> : null}
    </Stack>
  );
}
